import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../Hooks/useAPI";
import "./Register.css";
import { default as Header } from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import Input from "../../Components/Input/Input";
import Button2 from "../../Components/Button2/Button2";
import "../../Components/Page/Page.css";
import { AxiosError } from "axios";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import debounce from "lodash.debounce";

//images
import logo from "../../Images/Logo/White Background .png";
import text from "../../Images/Logo/IceGemsText.png";
import Rotator from "../../Components/Rotator/Rotator";

const PROFESSION_OPTIONS = [
  "Student",
  "Scientist",
  "Engineer",
  "Manufacturer",
  "Academic",
  "Government",
  "Defense",
  "Marketing",
  "Law",
  "Health",
  "Other",
];

const INTEREST_OPTIONS = [
  "Engines",
  "Software",
  "Research",
  "Manufacturing",
  "Public Policy",
  "Climate",
  "Economics",
  "Health",
  "Other",
];

interface RegisterFormData {
  email: string;
  password: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  streetaddress: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
  phone?: string;
  companyName?: string;
  companyType?: string;
  title?: string;
  profession: string[];
  professionOther?: string;
  interests: string[];
  interestsOther?: string;
  agreeToTerms: boolean;
  ipaddress: string;
  phonenocode: string;
  datasource: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  profession?: string;
  interests?: string;
  agreeToTerms?: string;
  general?: string;
}

// Define props for States
interface StateOption {
  stateprovinceabbrv: string;
  stateprovincename: string;
}

export default function Register() {
  const navigate = useNavigate();
  const {
    register,
    assignRoleToUser,
    getStateProvince,
    saveContact,
    checkEmailExists,
  } = useAPI();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: "",
    password: "",
    firstName: "",
    middleName: "",
    lastName: "",
    streetaddress: "",
    city: "",
    state: "",
    zip: "",
    country: "",
    phone: "",
    companyName: "",
    companyType: "",
    title: "",
    profession: [],
    professionOther: "",
    interests: [],
    interestsOther: "",
    agreeToTerms: false,
    ipaddress: "",
    phonenocode: "",
    datasource: "userregistration",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [, setEmail] = useState("");
  const [, setFirstName] = useState("");
  const [, setMiddleName] = useState("");
  const [, setLastName] = useState("");
  const [, setAddress] = useState("");
  const [, setCity] = useState("");
  const [, setState] = useState("");
  const [, setZip] = useState("");
  const [, setCountry] = useState("");
  const [, setPhone] = useState("");
  const [, setCountryCode] = useState("");
  const [, setCompanyName] = useState("");
  const [, setCompanyType] = useState("");
  const [, setTitle] = useState("");
  const [, setDataSourceDescription] = useState("");
  const [, setProfession] = useState<string[]>([]);
  const [, setProfessionOther] = useState("");
  const [, setInterests] = useState<string[]>([]);
  const [, setInterestsOther] = useState("");
  const [, setAgreeToTerms] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [, setPhoneNoCode] = useState("");
  const [states, setStates] = useState<StateOption[]>([]);
  const [cidn, setCIDN] = useState<number | null>(null);

  // --- Initialize existing contact ID from localStorage ---
  useEffect(() => {
    const storedId = Number(localStorage.getItem("partial_contact_id"));
    if (storedId) {
      setCIDN(storedId);
    }
  }, []);

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await getStateProvince();
        setStates(response);
      } catch (error) {
        console.error("Error loading states:", error);
      }
    };
    fetchStates();
  }, []);

  const fetchIp = async () => {
    try {
      const res = await fetch("https://api.ipify.org/?format=json");
      const data = await res.json();

      setFormData((prev) => ({
        ...prev,
        ipaddress: data.ip,
      }));

      console.log("Fetched IP:", data.ip);
    } catch (err) {
      console.error("IP fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchIp();
  }, []);

  const fieldRefs = {
    firstName: useRef<HTMLInputElement>(null),
    lastName: useRef<HTMLInputElement>(null),
    email: useRef<HTMLInputElement>(null),
    password: useRef<HTMLInputElement>(null),
    profession: useRef<HTMLDivElement>(null),
    interests: useRef<HTMLDivElement>(null),
    agreeToTerms: useRef<HTMLInputElement>(null),
  };

  const isFormValid = (): boolean => {
    return formData.agreeToTerms;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    if (type === "checkbox") {
      if (name === "agreeToTerms") {
        setFormData((prev) => {
          const updated = {
            ...prev,
            [name]: checked, // checkbox value
          };

          debouncedSaveRef(updated, cidn, name); // send updated data

          return updated;
        });
      } else if (name === "profession" || name === "interests") {
        const optionValue = value;

        setFormData((prev) => {
          const currentArray = prev[name] as string[];

          const updatedArray = checked
            ? [...currentArray, optionValue]
            : currentArray.filter((item) => item !== optionValue);

          const updated = {
            ...prev,
            [name]: updatedArray,
          };

          // Send to debounced auto-save with the REAL updated data
          debouncedSaveRef(updated, cidn, name);

          return updated;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === "age" ? (value ? Number(value) : undefined) : value,
      }));

      // Update form state
      if (name === "state") {
        setFormData((prev) => {
          const updated = { ...prev, [name]: value };

          // Send to debounced auto-save
          debouncedSaveRef(updated, cidn, name);

          return updated;
        });
      }
    }

    // Clear error for this field when user changes it
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePhoneChange = (
    value: string,
    data: { dialCode: string; countrycode: string }
  ) => {
    const countrycode = data.dialCode; // e.g. "1"
    const phonenocode = value.replace(`${countrycode}`, "");

    setPhoneNumber(value);
    setPhoneNoCode(phonenocode);

    setFormData((prev) => ({
      ...prev,
      phone: value,
      countrycode: countrycode,
      phonenocode: phonenocode,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }

    // Last Name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
      isValid = false;
    }

    // Profession validation
    if (formData.profession.length === 0) {
      newErrors.profession = "Please select at least one profession";
      isValid = false;
    }

    // Interests validation
    if (formData.interests.length === 0) {
      newErrors.interests = "Please select at least one interest";
      isValid = false;
    }

    // Terms & Conditions validation
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = "You must agree to the Terms & Conditions";
      isValid = false;
    }

    setErrors(newErrors);

    const firstErrorKey = Object.keys(newErrors)[0] as keyof typeof fieldRefs;

    const el = fieldRefs[firstErrorKey]?.current;

    if (el) {
      const headerOffset = 90; // header height
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      el.focus?.();
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear general error
    setErrors((prev) => ({ ...prev, general: undefined }));

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    const formDataToSubmit = {
      ...formData,
      cidn: cidn ?? undefined, // include it only if it exists
    };

    try {
      const data = await register(formDataToSubmit);

      // Store the token and user data
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      // Assign role "user-registered" (role id = 1) to the newly registered user
      if (data.user && data.user.id) {
        try {
          await assignRoleToUser(data.user.id, 1);
        } catch (roleErr) {
          console.error("Failed to assign role:", roleErr);
          // Continue with navigation even if role assignment fails
          navigate("/");
          //maybe nav home if issue happens
        }
      }

      // Redirect to home page
      navigate("/thank-you");
    } catch (err) {
      // Check if it's an Axios error with response data
      const axiosError = err as AxiosError<{ message: string }>;

      if (axiosError.response?.data?.message) {
        const errorMessage = axiosError.response.data.message;

        // Check for exact email already registered error
        if (errorMessage === "Email already registered") {
          // Set error on the email field
          setErrors((prev) => ({
            ...prev,
            email: "This Email is already registered",
          }));

          // Scroll to email field
          const el = fieldRefs.email.current;

          if (el) {
            const headerOffset = 90;
            const elementPosition = el.getBoundingClientRect().top;
            const offsetPosition =
              elementPosition + window.scrollY - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });

            el.focus();
          }

          return;
        } else {
          // Set as general error for other cases
          setErrors((prev) => ({
            ...prev,
            general: errorMessage,
          }));
        }
      } else {
        // Fallback for other types of errors
        setErrors((prev) => ({
          ...prev,
          general: err instanceof Error ? err.message : "An error occurred",
        }));
      }
    }
  };

  // --- Stable debounced saveContact function ---
  const debouncedSaveRef = React.useRef(
    debounce(
      async (
        updatedData: RegisterFormData,
        cidn: Number | null,
        fieldname: string | null
      ) => {
        try {
          //console.log("payload: ", updatedData);
          //console.log("fieldname: ", fieldname);

          const bReturnAll = fieldname === "email"; //set breturnall to true if fieldname is email

          const response = await saveContact(updatedData, bReturnAll, cidn);

          const out_cidn = response.out_cidn;

          //Only update all the fields in the page if we have a match by email address
          if (bReturnAll) {
            const returnedEmail = response?.email;
            const returnedFirstName = response?.firstname;
            const returnedMiddleName = response?.middlename;
            const returnedLastName = response?.lastname;
            const returnedAddress = response?.address1;
            const returnedCity = response?.city;
            const returnedState = response?.state;
            const returnedZip = response?.zip;
            const returnedCountry = response?.country;
            const returnedPhone = response?.phone;
            const returnedCountryCode = response?.countrycode;
            const returnedCompanyName = response?.companyname;
            const returnedCompanyType = response?.companytype;
            const returnedTitle = response?.title;
            const returnedDatasource = response?.datasourcedescription;
            const returnedProfession = response?.profession;
            const returnedProfessionOther = response?.profession_other;
            const returnedInterests = response?.interests;
            const returnedInterestsOther = response?.interests_other;
            const returnedAgreeToTerms = response?.termsconditions;

            const professionArray = parsePgArray(returnedProfession);
            const interestsArray = parsePgArray(returnedInterests);

            setEmail(returnedEmail);
            setFirstName(returnedFirstName);
            setMiddleName(returnedMiddleName);
            setLastName(returnedLastName);
            setAddress(returnedAddress);
            setCity(returnedCity);
            setState(returnedState);
            setZip(returnedZip);
            setCountry(returnedCountry);
            setPhone(returnedPhone);
            setPhoneNoCode(returnedPhone);
            if (returnedCountryCode && returnedPhone) {
              setPhoneNumber(`${returnedCountryCode}${returnedPhone}`);
            }
            setCountryCode(returnedCountryCode);
            setCompanyName(returnedCompanyName);
            setCompanyType(returnedCompanyType);
            setTitle(returnedTitle);
            setDataSourceDescription(returnedDatasource);
            setProfession(professionArray);
            setProfessionOther(returnedProfessionOther);
            setInterests(interestsArray);
            setInterestsOther(returnedInterestsOther);
            setAgreeToTerms(returnedAgreeToTerms);

            // Update the form field so the input shows it
            setFormData((prev) => ({
              ...prev,
              email: returnedEmail || "",
              firstName: returnedFirstName || "",
              middleName: returnedMiddleName || "",
              lastName: returnedLastName || "",
              streetaddress: returnedAddress || "",
              city: returnedCity || "",
              state: returnedState || "",
              zip: returnedZip || "",
              country: returnedCountry || "",
              phone: returnedPhone || "",
              phoneNumber: returnedPhone || "",
              countrycode: returnedCountryCode || "",
              companyName: returnedCompanyName || "",
              companyType: returnedCompanyType || "",
              title: returnedTitle || "",
              datasource: returnedDatasource || "userregistration",
              profession: professionArray,
              professionOther: returnedProfessionOther || "",
              interests: interestsArray,
              interestsOther: returnedInterestsOther || "",
              agreeToTerms: returnedAgreeToTerms || false,
            }));
          }

          // If new partial ID returned, store it
          const returnedId = out_cidn;

          if (returnedId && !cidn) {
            localStorage.setItem("partial_contact_id", returnedId);
            setCIDN(returnedId);
          }
        } catch (error) {
          console.error("Error auto-saving contact:", error);
        }
      },
      600
    ) // control debounce timing here
  ).current;

  const parsePgArray = (value: any): string[] => {
    if (!value) return [];

    // already a JS array
    if (Array.isArray(value)) return value;

    // If comes back as PG array: {"Engineer","Scientist"}
    if (typeof value === "string") {
      const trimmed = value.replace(/^{|}$/g, "").replace(/"/g, "");
      if (!trimmed) return [];
      return trimmed.split(",").map((v) => v.trim());
    }

    return [];
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Copy of updated form data
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // If the field is email and has a value, check for duplicates
      if (name === "email" && value) {
        (async () => {
          try {
            const exists = await checkEmailExists(value);

            if (exists) {
              console.log("Email already exists");
              setErrors((prevErrors) => ({
                ...prevErrors,
                email: "This Email is already registered",
              }));
            } else {
              //console.log("Email is available");
              setErrors((prevErrors) => ({
                ...prevErrors,
                email: "",
              }));
            }
          } catch (err) {
            console.error("Error checking email:", err);
          }
        })();
      }

      // Trigger debounced auto-save regardless of email check
      debouncedSaveRef(updated, cidn, name);

      return updated;
    });
  };

  useEffect(() => {
    const handleUnload = () => {
      localStorage.removeItem("partial_contact_id");
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      debouncedSaveRef.flush();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      debouncedSaveRef.cancel();
    };
  }, []);

  useEffect(() => {
    return () => {
      // Component is unmounting → user left the page
      localStorage.removeItem("partial_contact_id");
    };
  }, []);

  return (
    <div className="generalPage">
      <Header />
      <Rotator />
      <div className="pageContainer">
        <div className="register-container">
          <div className="register-card">
            <div className="register-header">
              <img
                loading="lazy"
                src={logo}
                className="logoImage"
                alt="IceGems logo"
              />
              <img
                loading="lazy"
                src={text}
                className="logoText"
                alt="IceGems logo"
              />
            </div>

            {errors.general && (
              <div className="error-message">{errors.general}</div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <Input
                  label="Email"
                  type="email"
                  id="email"
                  ref={fieldRefs.email}
                  placeholder="Enter your email"
                  required={true}
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.email && (
                  <div className="field-error">{errors.email}</div>
                )}
              </div>

              <div className="form-group">
                <Input
                  label="First Name"
                  type="text"
                  id="firstName"
                  ref={fieldRefs.firstName}
                  placeholder="Enter your first name"
                  required={true}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.firstName && (
                  <div className="field-error">{errors.firstName}</div>
                )}
              </div>

              <div className="form-group">
                <Input
                  label="Middle Name"
                  type="text"
                  id="middleName"
                  placeholder="Enter your middle name"
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="form-group" ref={fieldRefs.lastName}>
                <Input
                  label="Last Name"
                  type="text"
                  id="lastName"
                  placeholder="Enter your last name"
                  required={true}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                {errors.lastName && (
                  <div className="field-error">{errors.lastName}</div>
                )}
              </div>

              <div className="form-group">
                <Input
                  label="Password"
                  type="password"
                  id="password"
                  ref={fieldRefs.password}
                  placeholder="Enter your password"
                  required={true}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
                {errors.password && (
                  <div className="field-error">{errors.password}</div>
                )}
              </div>

              <div className="form-group">
                <Input
                  label="Address"
                  type="text"
                  id="streetaddress"
                  placeholder="Enter your address"
                  name="streetaddress"
                  value={formData.streetaddress}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="form-group">
                <Input
                  label="City"
                  type="text"
                  id="city"
                  placeholder="Enter your city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="form-group">
                <label className="selectdropdown">State</label>
                <select
                  id="state"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                >
                  <option value="">-- Select State --</option>
                  {states.map((s) => (
                    <option
                      key={s.stateprovinceabbrv}
                      value={s.stateprovinceabbrv}
                    >
                      {s.stateprovincename}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <Input
                  label="Zip Code"
                  type="text"
                  id="zip"
                  placeholder="Enter your zip code"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="form-group">
                <Input
                  label="Country"
                  type="text"
                  id="country"
                  placeholder="Enter your country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="form-group" id="phone">
                <label htmlFor="phone" className="inputLabel">
                  Phone Number
                </label>
                <PhoneInput
                  country={"us"}
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  onBlur={handleBlur}
                  inputClass="phone-input"
                  containerClass="phone-input-container"
                  buttonClass="phone-input-button"
                  dropdownClass="phone-input-dropdown"
                  searchClass="phone-input-search"
                  placeholder="Enter phone number"
                />
              </div>

              <div className="form-group">
                <Input
                  label="Company Name"
                  type="text"
                  id="companyName"
                  placeholder="Enter your company name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="form-group">
                <Input
                  label="Company Type"
                  type="text"
                  id="companyType"
                  placeholder="Enter your company type"
                  name="companyType"
                  value={formData.companyType}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="form-group">
                <Input
                  label="Title"
                  type="text"
                  id="title"
                  placeholder="Enter your title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="form-group">
                <label>Profession (Select at least one)</label>
                <div className="checkbox-group">
                  <div ref={fieldRefs.profession}>
                    {PROFESSION_OPTIONS.map((option) => (
                      <div key={option} className="checkbox-item">
                        <label htmlFor={`profession-${option}`}>
                          <input
                            type="checkbox"
                            id={`profession-${option}`}
                            name="profession"
                            value={option}
                            checked={formData.profession.includes(option)}
                            onChange={handleChange}
                            className={errors.profession ? "error" : ""}
                          />{" "}
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                {errors.profession && (
                  <div className="field-error">{errors.profession}</div>
                )}
              </div>

              {formData.profession.includes("Other") && (
                <div className="form-group">
                  <Input
                    label="Other Profession"
                    type="text"
                    id="professionOther"
                    placeholder="Please specify your other profession"
                    name="professionOther"
                    value={formData.professionOther}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Interests (Select at least one)</label>
                <div className="checkbox-group">
                  <div ref={fieldRefs.interests}>
                    {INTEREST_OPTIONS.map((option) => (
                      <div key={option} className="checkbox-item">
                        <label htmlFor={`interests-${option}`}>
                          <input
                            type="checkbox"
                            id={`interests-${option}`}
                            name="interests"
                            value={option}
                            checked={formData.interests.includes(option)}
                            onChange={handleChange}
                            className={errors.interests ? "error" : ""}
                          />{" "}
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                {errors.interests && (
                  <div className="field-error">{errors.interests}</div>
                )}
              </div>

              {formData.interests.includes("Other") && (
                <div className="form-group">
                  <Input
                    label="Other Interests"
                    type="text"
                    id="interestsOther"
                    placeholder="Please specify your other interests"
                    name="interestsOther"
                    value={formData.interestsOther}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              )}

              <div className="terms-checkbox-container">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  name="agreeToTerms"
                  ref={fieldRefs.agreeToTerms}
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className={`terms-checkbox ${
                    errors.agreeToTerms ? "checkbox-error" : ""
                  }`}
                />
                <label htmlFor="agreeToTerms" className="terms-label">
                  I agree to the{" "}
                  <a
                    href="/documents/ICEGEMS_Terms-and-Conditions.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="terms-link"
                  >
                    Terms & Conditions*
                  </a>
                </label>
              </div>
              {errors.agreeToTerms && (
                <div className="field-error terms-error">
                  {errors.agreeToTerms}
                </div>
              )}

              <button
                id="submit"
                type="submit"
                className={`submit-button ${
                  isFormValid() ? "submit-button-active" : ""
                }`}
                disabled={!isFormValid()}
              >
                Register
              </button>
            </form>

            <div className="register-link">
              <p className="register-text">
                Already have an account?
                <Button2
                  href="/user-login"
                  name="login"
                  text="Sign In"
                  type="button"
                />
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
